
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BookOpen, Download, File, FileText, GraduationCap, List, Search } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";

// Sample data for knowledge resources
const recommendedReading = [
  {
    id: 1,
    title: "Zero to One",
    author: "Peter Thiel",
    description: "Notes on startups, or how to build the future",
    category: "Book",
    difficulty: "Beginner",
    timeToRead: "4-6 hrs"
  },
  {
    id: 2,
    title: "The Lean Startup",
    author: "Eric Ries",
    description: "How today's entrepreneurs use continuous innovation to create radically successful businesses",
    category: "Book",
    difficulty: "Beginner",
    timeToRead: "5-7 hrs"
  },
  {
    id: 3,
    title: "The Future of VC Funding",
    author: "Harvard Business Review",
    description: "New investment models and what they mean for founders",
    category: "Article",
    difficulty: "Intermediate",
    timeToRead: "15 mins"
  },
  {
    id: 4,
    title: "Financial Modeling for Startups",
    author: "Y Combinator",
    description: "A practical guide to revenue forecasting and expense planning",
    category: "Guide",
    difficulty: "Advanced",
    timeToRead: "2-3 hrs"
  },
];

const templates = [
  {
    id: 1,
    title: "Business Model Canvas",
    description: "A strategic management template for developing new or documenting existing business models",
    category: "Framework",
    format: "PDF"
  },
  {
    id: 2,
    title: "Pitch Deck Template",
    description: "A professionally designed template with guidance for each slide",
    category: "Presentation",
    format: "PPT"
  },
  {
    id: 3,
    title: "Financial Projection Spreadsheet",
    description: "3-year projection template with pre-built formulas and charts",
    category: "Spreadsheet",
    format: "XLS"
  },
  {
    id: 4,
    title: "Product Development Roadmap",
    description: "Visual template for planning your product journey from MVP to full feature set",
    category: "Planning",
    format: "PDF"
  },
];

// Resource card component
const ResourceCard = ({ item }: { item: typeof recommendedReading[0] }) => {
  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center mb-1">
              <BookOpen className="h-4 w-4 mr-1 text-primary" />
              <span className="text-xs font-medium">{item.category}</span>
              <span className="mx-1 text-muted-foreground">•</span>
              <span className="text-xs text-muted-foreground">{item.timeToRead}</span>
            </div>
            <CardTitle className="text-lg">{item.title}</CardTitle>
            <div className="text-sm text-muted-foreground mt-1">by {item.author}</div>
          </div>
          <div className="px-2 py-1 bg-secondary text-secondary-foreground text-xs rounded-md">
            {item.difficulty}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <CardDescription>
          {item.description}
        </CardDescription>
      </CardContent>
      <CardFooter className="justify-between">
        <Button variant="ghost" className="gap-1">
          <BookOpen className="h-4 w-4" /> Read
        </Button>
        <Button variant="outline" size="sm">Save</Button>
      </CardFooter>
    </Card>
  );
};

// Template card component
const TemplateCard = ({ item }: { item: typeof templates[0] }) => {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center mb-1">
          <FileText className="h-4 w-4 mr-1 text-primary" />
          <span className="text-xs font-medium">{item.category}</span>
          <span className="mx-1 text-muted-foreground">•</span>
          <span className="text-xs text-muted-foreground">{item.format}</span>
        </div>
        <CardTitle className="text-lg">{item.title}</CardTitle>
      </CardHeader>
      <CardContent>
        <CardDescription>
          {item.description}
        </CardDescription>
      </CardContent>
      <CardFooter className="justify-between">
        <Button variant="ghost" className="gap-1">
          <Download className="h-4 w-4" /> Download
        </Button>
        <Button variant="outline" size="sm">Preview</Button>
      </CardFooter>
    </Card>
  );
};

const KnowledgeHub = () => {
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold tracking-tight">Knowledge Hub</h2>
        <div className="flex space-x-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input 
              type="search" 
              placeholder="Search resources..." 
              className="pl-8 w-[200px] md:w-[300px]" 
            />
          </div>
        </div>
      </div>
      
      <Tabs defaultValue="reading">
        <TabsList className="mb-4">
          <TabsTrigger value="reading" className="gap-1">
            <BookOpen className="h-4 w-4" /> Recommended Reading
          </TabsTrigger>
          <TabsTrigger value="learning" className="gap-1">
            <GraduationCap className="h-4 w-4" /> Learning Resources
          </TabsTrigger>
          <TabsTrigger value="templates" className="gap-1">
            <File className="h-4 w-4" /> Templates & Frameworks
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="reading" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {recommendedReading.map((resource) => (
              <ResourceCard key={resource.id} item={resource} />
            ))}
          </div>
          <div className="flex justify-center">
            <Button variant="outline">View More Books & Articles</Button>
          </div>
        </TabsContent>
        
        <TabsContent value="learning" className="space-y-4">
          <div className="rounded-xl border bg-card text-card-foreground">
            <div className="flex flex-col space-y-1.5 p-6">
              <h3 className="font-semibold leading-none tracking-tight">
                Learning Paths
              </h3>
              <p className="text-sm text-muted-foreground">
                Structured learning resources to build your entrepreneurial skills
              </p>
            </div>
            <div className="p-6 pt-0">
              <div className="rounded-md border">
                <div className="flex flex-col divide-y">
                  <div className="flex items-center p-4">
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center mr-4">
                      <GraduationCap className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium">First-Time Founder Essentials</h4>
                      <p className="text-sm text-muted-foreground">8 modules • Beginner friendly</p>
                    </div>
                    <Button>Start Learning</Button>
                  </div>
                  <div className="flex items-center p-4">
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center mr-4">
                      <List className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium">Product Market Fit Masterclass</h4>
                      <p className="text-sm text-muted-foreground">6 modules • Intermediate</p>
                    </div>
                    <Button>Start Learning</Button>
                  </div>
                  <div className="flex items-center p-4">
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center mr-4">
                      <FileText className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium">Scaling Your Startup</h4>
                      <p className="text-sm text-muted-foreground">10 modules • Advanced</p>
                    </div>
                    <Button>Start Learning</Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="templates" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {templates.map((template) => (
              <TemplateCard key={template.id} item={template} />
            ))}
          </div>
          <div className="flex justify-center">
            <Button variant="outline">Browse All Templates</Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default KnowledgeHub;
